import mapValues from 'lodash/mapValues';
import round from 'utils/round';
import formatSecondsShort from 'utils/formatSecondsShort';
import {MetricType, StatType} from 'sources/dashboard/DashMetricsSource';
import sum from 'utils/sum';
import average from 'utils/average';
import CoachingReviewTypeStore, {categories} from 'stores/coaching/CoachingReviewTypeStore';
import React from 'react';
import {MeetingCategoriesDropdown} from 'components/dashboard/common/MeetingCategoriesDropdown';

const formatTime = value =>
  isNaN(value) || value === 'NaN' ? 'N/A' : formatSecondsShort(value, true, false, true);
const formatPct = value => (isNaN(value) || value === 'NaN' ? 'N/A' : round(value * 100) + '%');
const formatRound = value => (isNaN(value) || value === 'NaN' ? 'N/A' : round(value));
const summarySum = values => sum(values);
const summaryAvg = values => average(values);

const secondaryDimensionFilter = ({
  secondaryDimension,
  teamIds,
  startDate,
  endDate,
  direction,
  fetchMetricSource,
  metric,
}) =>
  fetchMetricSource(metric, {
    context: secondaryDimension,
    teamIds,
    startDate,
    endDate,
    direction,
    stat: StatType.per,
  });

// ***************************************************************************
// The default selected subtab should have the same name as its parent object.
// ***************************************************************************
export const coachingTabConfigs = mapValues(
  {
    [MetricType.listeningCount]: {
      label: 'Listening',
      subtabs: {
        [MetricType.listeningCount]: {
          label: 'Calls Total',
        },
        [MetricType.listeningPercent]: {
          label: 'Average',
          formatValue: formatPct,
        },
        [MetricType.listeningTime]: {
          label: 'Time Total',
          formatValue: formatTime,
        },
      },
    },
    [MetricType.commentsCount]: {
      label: 'Comments',
      subtabs: {
        [MetricType.commentsCount]: {
          label: 'Comments Total',
        },
        [MetricType.commentsLength]: {
          label: 'Total Length',
        },
      },
    },
    [MetricType.highlightsCount]: {
      label: 'Highlights',
    },
    [MetricType.callsScored]: {
      label: 'Scoring',
    },
    [MetricType.libraryAdds]: {
      label: 'Libraries',
      chartTitle: 'calls added to libraries',
    },
    [MetricType.plans]: {
      label: 'Coaching Plans',
    },
    /**
     * Coaching meetings can be filtered further by a specific meeting category.
     * We provide these three generic arguments to allow for this (only used by coaching meetings,
     * but available to all - easier than creating a special snowflake just for meetings)
     */
    [MetricType.meetings]: {
      label: 'Coaching Meetings',
      secondaryDimensionOptionRenderer: ({onSelect, options, value, ...props}) => (
        <MeetingCategoriesDropdown
          meetingCategoryId={value}
          placeholder={'Filter by meeting type...'}
          meetingCategories={options}
          onChange={onSelect}
          width={300}
          allowReset
          {...props}
        />
      ),
      secondaryDimensionOptions: () => {
        const [, cats] = categories(CoachingReviewTypeStore);
        return cats.toList();
      },
      secondaryDimensionColorFromOption: id => {
        if (!id) return undefined;

        const [, cats] = categories(CoachingReviewTypeStore);
        return cats.get(id).color;
      },
      secondaryDimensionFilter,
    },
  },
  config => ({
    chartTitle: config.label,
    formatValue: formatRound,
    summary: summarySum,
    ...config,
    subtabs: mapValues(config.subtabs, subtabConfig => ({
      chartTitle: subtabConfig.label,
      formatValue: formatRound,
      ...subtabConfig,
    })),
  })
);

export const callTabConfigs = mapValues(
  {
    [MetricType.totalDuration]: {
      label: 'Total Duration',
      summary: summarySum,
      formatValue: formatTime,
    },
    [MetricType.conversations]: {
      label: 'Conversations',
      summary: summarySum,
    },
    [MetricType.avgDuration]: {
      label: 'Avg Duration',
      formatValue: formatTime,
      summary: summaryAvg,
    },
    [MetricType.talkListen]: {
      label: 'Talk / Listen Ratio',
      formatValue: formatPct,
      summary: summaryAvg,
    },
    [MetricType.interchanges]: {
      label: 'Interchanges Per Minute',
      summary: summaryAvg,
    },
  },
  config => ({
    chartTitle: config.label,
    formatValue: formatRound,
    ...config,
  })
);
